import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Save } from 'lucide-react';
import { commandId, readCkScope, scopeKey, useCkScope } from '../components/CkOperationsShared';
import { createReceivingApi } from './api';
import Evidence from './Evidence';
import Reconciliation from './Reconciliation';
import { enqueue } from './offlineQueue';
import './receiving.css';

const draftOf = line => ({ received_qty: line?.received_qty ?? '', lot_ref: line?.lot_ref ?? '',
  use_by: line?.use_by ?? '', probe_temp_c: line?.probe_temp_c ?? '', reason_code: line?.reason_code ?? '', evidence: [...(line?.evidence || [])] });

export default function ReceiptWorkspace({ userId, receiptId }) {
  const scope = useCkScope();
  return <Workspace key={`${userId}:${receiptId}:${scopeKey(scope)}`} userId={userId} receiptId={receiptId} scope={scope} />;
}

function Workspace({ userId, receiptId, scope }) {
  const [receipt, setReceipt] = useState(null);
  const [index, setIndex] = useState(0);
  const [draft, setDraft] = useState(draftOf());
  const [dirty, setDirty] = useState(false);
  const [draftVersion, setDraftVersion] = useState(null);
  const [canOperate, setCanOperate] = useState(false);
  const [canSelfConfirm, setCanSelfConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [storageFailed, setStorageFailed] = useState(false);
  const [evidenceBusy, setEvidenceBusy] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [offlineQueued, setOfflineQueued] = useState(false);
  const storageKey = JSON.stringify(['receiving-line-draft', userId, scope.orgId, scope.locationId, scope.department, receiptId]);
  const submissionKey = JSON.stringify(['receiving-submit', storageKey]);
  const confirmationKey = JSON.stringify(['receiving-confirm', storageKey]);
  const alive = useRef(false), lock = useRef(false);
  const api = useMemo(() => createReceivingApi(() => ({ ...readCkScope(), userId })), [userId]);
  const active = useCallback(() => alive.current && scopeKey(readCkScope()) === scopeKey(scope), [scope]);
  const load = useCallback(async (discard = false) => {
    if (!active() || lock.current) return;
    lock.current = true; setBusy(true); setError(''); setNotice(''); setReceipt(null); setCanOperate(false);
    try {
      const caps = await api.capabilities();
      const doc = await api.receipt(receiptId);
      if (!active()) return;
      if (discard) sessionStorage.removeItem(storageKey);
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      const submit = JSON.parse(sessionStorage.getItem(submissionKey) || 'null');
      const confirm = JSON.parse(sessionStorage.getItem(confirmationKey) || 'null');
      if (submit && (typeof submit.command_id !== 'string' || !/^[A-Za-z0-9_.:-]{1,128}$/.test(submit.command_id) || !Number.isInteger(submit.version) || submit.version < 1)) {
        throw new Error('Stored submission command is invalid. Submission recovery requires support.');
      }
      setSubmission(submit);
      setConfirmation(confirm);
      const selected = saved ? doc.lines.findIndex(row => row.line_index === saved.lineIndex) : 0;
      if (saved && (selected < 0 || !saved.draft || !Number.isInteger(saved.version))) throw new Error('Stored draft is invalid. Clear it with Reload receipt.');
      setReceipt(doc); setIndex(selected); setDraft(saved?.draft || draftOf(doc.lines[0])); setDirty(!!saved?.dirty);
      setDraftVersion(saved?.version ?? doc.version);
      setCanOperate(caps.can_operate === true); setCanSelfConfirm(caps.can_self_confirm_receiving === true); setPending(saved?.pending || null);
      const writable = caps.can_operate === true && doc.operator_id === userId && ['open', 'needs_correction'].includes(doc.status);
      const changed = !!saved && ((!saved.pending && saved.version !== doc.version) || !writable);
      setConflict(changed); setStorageFailed(false);
      if (changed) setError('Receipt changed while this draft was away. Reload to discard the draft and reconcile.');
    } catch (e) { if (active()) setError(e.message); }
    finally { lock.current = false; if (active()) setBusy(false); }
  }, [active, api, confirmationKey, receiptId, storageKey, submissionKey, userId]);
  useEffect(() => { alive.current = true; load(); return () => { alive.current = false; }; }, [load]);
  useEffect(() => {
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    if (dirty || pending || submission || confirmation || evidenceBusy) window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, pending, submission, confirmation, evidenceBusy]);
  useEffect(() => {
    if (!receipt || !active()) return;
    try {
      if (dirty || pending) sessionStorage.setItem(storageKey, JSON.stringify({ version: draftVersion,
        lineIndex: receipt.lines[index].line_index, draft, dirty, pending }));
      else sessionStorage.removeItem(storageKey);
    } catch { setStorageFailed(true); setError('Session draft storage is unavailable. Saving is blocked until storage is available.'); }
  }, [receipt, index, draft, dirty, pending, draftVersion, storageKey, active]);

  const editable = canOperate && receipt?.operator_id === userId && ['open', 'needs_correction'].includes(receipt?.status);
  const line = receipt?.lines[index];
  function select(value) {
    if (busy || evidenceBusy || pending || submission || conflict || (dirty && !window.confirm('Discard unsaved line changes?'))) return;
    setIndex(value); setDraft(draftOf(receipt.lines[value])); setDraftVersion(receipt.version); setDirty(false); setError(''); setNotice('');
  }
  function reload() {
    if (evidenceBusy) return;
    if (pending && !conflict) { setError('Retry the pending save before reloading this receipt.'); return; }
    if (dirty && !window.confirm('Discard unsaved changes and load the current receipt?')) return;
    load(true);
  }
  async function save(event) {
    event.preventDefault();
    if (!active() || lock.current || evidenceBusy || submission || !editable || conflict || storageFailed || !line) return;
    let command = pending;
    if (!command) {
      if (!/^\d+(\.\d+)?$/.test(draft.received_qty.trim()) ||
          (draft.probe_temp_c.trim() && !/^-?\d+(\.\d+)?$/.test(draft.probe_temp_c.trim()))) {
        setError('Enter a nonnegative received quantity and a decimal temperature if measured.'); return;
      }
      try {
        command = { index: line.line_index, body: { command_id: commandId(), version: receipt.version,
          received_qty: draft.received_qty.trim(), probe_temp_c: draft.probe_temp_c.trim() || null,
          lot_ref: draft.lot_ref.trim() || null, use_by: draft.use_by || null, reason_code: draft.reason_code || null,
          section_id: line.section_id ?? null, evidence: [...(draft.evidence || line.evidence || [])] } };
      } catch { setError('This browser cannot create a secure save reference. Nothing was sent.'); return; }
    }
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ version: receipt.version, lineIndex: line.line_index, draft, dirty: true, pending: command }));
    } catch { setStorageFailed(true); setError('Could not preserve the retry command. Nothing was sent.'); return; }
    lock.current = true; setBusy(true); setPending(command); setError(''); setNotice('');
    try {
      const doc = await api.saveLine(receiptId, command.index, command.body);
      if (!active()) return;
      setReceipt(doc); setDraft(draftOf(doc.lines[index])); setDraftVersion(doc.version); setPending(null); setDirty(false); setNotice('Line saved.');
    } catch (e) {
      if (!active()) return;
      if (e.status === 409) { setConflict(true); setError('Receipt changed. Reload the current receipt before editing again.'); }
      else if ([401, 403, 404].includes(e.status)) { setConflict(true); setError(`${e.message} Reload after restoring access to reconcile this save.`); }
      else if ([400, 422].includes(e.status)) { setPending(null); setError(e.message); }
      else { setError(`${e.message} Save outcome is not confirmed. Retry the same save.`); }
    } finally { lock.current = false; if (active()) setBusy(false); }
  }
  async function submit(review) {
    if (!active() || lock.current || evidenceBusy || pending || dirty || conflict || storageFailed || !canOperate || receipt?.operator_id !== userId) return;
    let command = submission;
    if (!command) {
      if (!editable || review?.receipt_id !== receiptId || review.version !== receipt.version || review.status !== receipt.status || !review.complete ||
          review.lines.some(row => row.needs_review && (!row.reason_code || !row.evidence?.length))) return;
      try {
        for (const row of receipt.lines) {
          if (sessionStorage.getItem(JSON.stringify(['receiving-upload', storageKey, row.line_index]))) {
            setError('Resolve pending evidence uploads before submitting.'); return;
          }
        }
        if (!window.confirm('Submit this saved receipt for review? Stock is not posted until confirmation.')) return;
        command = { command_id: commandId(), version: review.version };
      } catch { setError('Submission recovery storage or command generation is unavailable. Nothing was sent.'); return; }
    }
    try { sessionStorage.setItem(submissionKey, JSON.stringify(command)); }
    catch { setError('Could not preserve the submission command. Nothing was sent.'); return; }
    lock.current = true; setBusy(true); setSubmission(command); setError(''); setNotice('');
    let accepted = false;
    try {
      await api.submit(receiptId, command);
      if (!active()) return;
      sessionStorage.removeItem(submissionKey); setSubmission(null); accepted = true;
    } catch (e) {
      if (!active()) return;
      if ([400, 409, 422].includes(e.status)) {
        try { sessionStorage.removeItem(submissionKey); setSubmission(null); }
        catch { setStorageFailed(true); }
        setConflict(true); setError(`${e.message} Reload the receipt and review again.`);
      } else {
        if ([401, 403, 404].includes(e.status)) setConflict(true);
        setError(`${e.message} Submission outcome is unconfirmed. Restore access if needed, reload and retry the same submission.`);
      }
    } finally { lock.current = false; if (active()) setBusy(false); }
    if (accepted && active()) {
      await load(true);
      if (active()) setNotice('Submission accepted.');
    }
  }
  async function selfConfirm(review) {
    if (!active() || lock.current || evidenceBusy || pending || dirty || conflict || storageFailed || !canSelfConfirm || receipt?.operator_id !== userId || receipt?.status !== 'submitted') return;
    let command = confirmation;
    if (!command) {
      if (!review || review.version !== receipt.version || review.requires_review || !review.complete) return;
      if (!window.confirm('Confirm this exact delivery and post it to stock?')) return;
      command = { command_id: commandId(), version: review.version };
    }
    try { sessionStorage.setItem(confirmationKey, JSON.stringify(command)); }
    catch { setStorageFailed(true); setError('Could not preserve the confirmation command. Nothing was sent.'); return; }
    lock.current = true; setBusy(true); setConfirmation(command); setError(''); setNotice('');
    let accepted = false;
    try { await api.confirm(receiptId, command); if (!active()) return; sessionStorage.removeItem(confirmationKey); setConfirmation(null); accepted = true; }
    catch (e) { if (!active()) return; if ([400, 409, 422].includes(e.status)) { sessionStorage.removeItem(confirmationKey); setConfirmation(null); setConflict(true); setError(`${e.message} Reload and review the current receipt.`); } else { if ([401, 403, 404].includes(e.status)) setConflict(true); setError(`${e.message} Confirmation outcome is unconfirmed. Retry the same confirmation after checking stock.`); } }
    finally { lock.current = false; if (active()) setBusy(false); }
    if (accepted && active()) { await load(true); if (active()) setNotice('Receipt confirmed and stock posted.'); }
  }
  async function queueOffline(kind) {
    const command = kind === 'save_line' ? pending && { kind, command_id: pending.body.command_id, receipt_id: receiptId, line_index: pending.index, body: pending.body }
      : submission && { kind, command_id: submission.command_id, receipt_id: receiptId, body: submission };
    if (!command || !window.confirm('Store this exact command for manual sync on this device? Evidence files are not stored offline.')) return;
    try {
      await enqueue({ userId, orgId: scope.orgId, locationId: scope.locationId, department: scope.department }, command);
      if (kind === 'save_line') { sessionStorage.removeItem(storageKey); setPending(null); setDirty(false); }
      else { sessionStorage.removeItem(submissionKey); setSubmission(null); }
      setConflict(true); setOfflineQueued(true); setNotice('Command queued for manual offline sync.'); setError('');
    } catch (e) { setError(e.message); }
  }
  const field = (name, label, props = {}) => <label>{label}<input {...props} value={draft[name]} onChange={e => { setDraft({ ...draft, [name]: e.target.value }); setDirty(true); setNotice(''); }} /></label>;
  return <main className="receiving-queue" aria-label="Receipt line entry">
    <header className="receiving-heading"><div><p className="receiving-eyebrow">Receiving</p><h1>Receipt lines</h1></div>
      <button title="Reload receipt" aria-label="Reload receipt" disabled={busy || evidenceBusy} onClick={reload}><RefreshCw size={20} /></button></header>
    <nav className="receiving-links" aria-label="Receiving navigation"><a href="/m/inbox">My receipts</a></nav>
    {error && <p role="alert" className="receiving-error">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {busy && <p role="status">Saving or loading receipt...</p>}
    {receipt && <>
      <p>{receipt.po_number || receipt.po_id} · {receipt.supplier_name || 'Supplier not specified'}</p>
      <p>Status: {receipt.status.replaceAll('_', ' ')} · Version {receipt.version}</p>
      {receipt.review_note && <p className="receiving-error">Correction: {receipt.review_note}</p>}
      <section className="receiving-arrival">
        <label>Receipt line<select aria-label="Receipt line" value={index} disabled={busy || evidenceBusy || !!pending || !!submission || conflict} onChange={e => select(Number(e.target.value))}>
          {receipt.lines.map((row, i) => <option key={row.line_index} value={i}>{i + 1}. {row.description || row.ingredient_id || 'Item'}</option>)}
        </select></label>
        {line && <><h2>{line.description || line.ingredient_id}</h2><p>Expected: {line.expected_qty} {line.uom}</p>
          <p>Saved temperature check: {line.temp_ok ? 'Passed' : 'Not verified / outside limits'}</p>
          <p>Evidence files: {(line.evidence || []).length}</p>
          {editable ? <form onSubmit={save} aria-label="Save receipt line"><fieldset disabled={busy || !!pending || !!submission || conflict}>
            {field('received_qty', `Received quantity (${line.uom})`, { inputMode: 'decimal', required: true })}
            {field('lot_ref', 'Supplier lot', { maxLength: 200 })}
            {field('use_by', 'Use by', { type: 'date' })}
            {field('probe_temp_c', 'Probe temperature C', { inputMode: 'decimal' })}
            <label>Discrepancy reason<select value={draft.reason_code} onChange={e => { setDraft({ ...draft, reason_code: e.target.value }); setDirty(true); }}>
              <option value="">No reason recorded</option><option value="short">Short delivery</option><option value="damaged">Damaged</option><option value="temperature">Temperature</option><option value="other">Other</option>
            </select></label>
          </fieldset><button disabled={busy || evidenceBusy || !!submission || conflict || storageFailed} type="submit"><Save size={18} />{pending ? 'Retry same save' : 'Save line'}</button>
          {dirty && <p role="status">Unsaved changes</p>}</form> : <p>Read-only. Received: {line.received_qty ?? 'Not recorded'} {line.uom}</p>}
        </>}
      </section>
      {line && <Evidence key={line.line_index} receiptId={receiptId} lineIndex={line.line_index} version={receipt.version} ids={draft.evidence || line.evidence || []}
        savedIds={line.evidence || []} journalKey={JSON.stringify(['receiving-upload', storageKey, line.line_index])}
        api={api} active={active} editable={editable} disabled={busy || !!pending || !!submission || conflict || storageFailed}
        onBusy={setEvidenceBusy} onUploaded={id => { setDraft(previous => ({ ...previous, evidence: [...new Set([...(previous.evidence || line.evidence || []), id])] })); setDirty(true); setNotice(''); }} />}
      <Reconciliation key={`summary-${receipt.version}`} receiptId={receiptId} version={receipt.version} status={receipt.status} api={api} active={active}
        disabled={busy || evidenceBusy || dirty || !!pending || !!submission || !!confirmation || conflict || storageFailed} canSubmit={editable} onSubmit={submit}
        canConfirm={canSelfConfirm && receipt.operator_id === userId && receipt.status === 'submitted'} onConfirm={selfConfirm} />
      {submission && <section className="receiving-arrival" aria-label="Submission recovery"><p>Submission outcome not confirmed. Reviewed version: {submission.version}.</p>
        {canOperate && receipt.operator_id === userId && <button type="button" disabled={busy || evidenceBusy || dirty || !!pending || conflict || storageFailed} onClick={() => submit()}>Retry same submission</button>}
        <button type="button" disabled={busy || evidenceBusy || storageFailed} onClick={() => queueOffline('submit')}>Queue for offline sync</button>
      </section>}
      {pending && <section className="receiving-arrival"><p>Save outcome is not confirmed.</p><button type="button" disabled={busy || evidenceBusy || storageFailed} onClick={() => queueOffline('save_line')}>Queue this save for offline sync</button></section>}
      {confirmation && <section className="receiving-arrival"><p>Confirmation outcome is not confirmed. Check current stock before retrying.</p><button type="button" disabled={busy || evidenceBusy || conflict || storageFailed} onClick={() => selfConfirm()}>Retry same confirmation</button></section>}
      {offlineQueued && <p><a href="/m/sync">Open offline sync</a></p>}
    </>}
  </main>;
}
